import { format } from 'date-fns';

import {
  IKbEnvelope,
  TKbSyncStatus,
  TKbSyncTone,
} from '@/types/knowledgeBase.types';

/**
 * `syncKbWebsiteUrls` crawls every active source inline (~2s each, ~24s at 12
 * sources), so it needs a far longer budget than a normal request.
 */
export const KB_SYNC_TIMEOUT_MS = 30_000;

/**
 * KB resolvers answer 200 OK and put the real outcome in `statusCode` inside
 * `data` — a denied call is `statusCode: 403` with null fields, not a GraphQL
 * error. Never treat an empty `errors` array as success.
 */
export const isKbOk = (result?: IKbEnvelope | null): boolean =>
  result?.statusCode === 200;

/** Human-readable reason for a non-200 envelope. */
export const kbErrorMessage = (
  result?: IKbEnvelope | null,
  fallback = 'Something went wrong.',
): string => {
  if (!result) return 'The server returned an empty response.';
  if (result.message) return result.message;

  switch (result.statusCode) {
    case 400:
      return 'The request was rejected as invalid.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'That record no longer exists.';
    case 409:
      return 'That URL is already registered.';
    default:
      return fallback;
  }
};

/**
 * Applies a hard timeout to a single Apollo operation. HttpLink forwards
 * `fetchOptions` straight to fetch, so an AbortController is all that is needed.
 */
export const withKbTimeout = async <T>(
  timeoutMs: number,
  run: (fetchOptions: RequestInit) => Promise<T>,
): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await run({ signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Apollo surfaces a timeout as an ApolloError wrapping the fetch AbortError, so
 * the cause has to be unwrapped before it can be reported as a timeout.
 */
export const isAbortError = (error: unknown): boolean => {
  const candidate = error as
    | { name?: string; networkError?: { name?: string } }
    | null
    | undefined;

  return (
    candidate?.name === 'AbortError' ||
    candidate?.networkError?.name === 'AbortError'
  );
};

/**
 * Maps the API's status vocabulary onto display tones. Anything unrecognised is
 * `unknown`, never `error` — inventing a failure verdict for a status we simply
 * do not know is how `indexed` ended up reported as a failed crawl.
 */
export const kbSyncStatusTone = (status: TKbSyncStatus): TKbSyncTone => {
  switch (status?.toLowerCase()) {
    case 'indexed':
      return 'success';
    case 'empty':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'unknown';
  }
};

export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

/** Tolerates ISO strings and epoch-millisecond strings alike. */
export const formatKbDate = (value?: string | null): string => {
  if (!value) return '—';

  const parsed = /^\d+$/.test(value)
    ? new Date(Number(value))
    : new Date(value);

  if (Number.isNaN(parsed.getTime())) return '—';
  return format(parsed, 'MMM dd, yyyy HH:mm');
};

/** Rejects anything that is not an absolute http(s) URL. */
export const validateKbUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return 'Enter a URL.';

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'Enter a full URL, including https://';
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'Only http:// and https:// URLs can be crawled.';
  }
  return null;
};
